import { isEmpty } from "lodash";
import { Form, Formik } from "formik";
import Button, { BUTTON_SIZES, BUTTON_VARIANTS } from "app/pages/.shared/form/Button";
import {
	ALERT_TYPE,
	LIB_PHONE_NUMBER_TYPES,
	NEWSLETTER_FREQUENCIES,
	NEWSLETTER_REMARKETING,
	NEWSLETTER_SMS,
	PROFILE_CODE_STATUS,
} from "app/constants";
import { FormattedMessage } from "react-intl";
import PropTypes from "prop-types";
import AlertMessage from "app/pages/.shared/AlertMessage";
import PhoneIntl from "app/pages/.shared/profile/PhoneIntl";
import { object, string } from "yup";
import { validatePhoneNumberType } from "app/pages/Account/Profile/profileSchema";
import FormErrorMessages from "app/pages/.shared/form/FormErrorMessages/FormErrorMessages";
import Loader from "app/pages/.shared/Loader";
import RadioFormik from "app/pages/.shared/form/RadioFormik";
import "./NewsLetterFrequency.scss";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import classNames from "classnames";

const validationSchema = displaySmsInput => {
	return object().shape({
		frequency: string().required({
			id: "error.frequency.required",
		}),
		password: string(),
		remarketing: string().required({
			id: "error.frequency.required",
		}),
		sms:
			displaySmsInput &&
			string().required({
				id: "error.frequency.required",
			}),
		phone: string().when("sms", {
			is: "accept",
			then: schema =>
				schema
					.required({
						id: "error.phone.required",
					})
					.test(
						"should be a mobile phone number",
						{ id: "error.phone.mobile.is.fixed" },
						numberIntl =>
							validatePhoneNumberType(numberIntl, [
								LIB_PHONE_NUMBER_TYPES.PERSONAL_NUMBER,
								LIB_PHONE_NUMBER_TYPES.MOBILE,
							])
					),
		}),
	});
};

const NewsLetterFrequency = ({
	handleSubmit = () => {},
	initialValues = {},
	shop,
	loading,
	displaySmsInput = true,
}) => {
	if (loading === PROFILE_CODE_STATUS.LOADING) {
		return <Loader />;
	}

	return !isEmpty(initialValues) ? (
		<Formik
			initialValues={initialValues}
			initialStatus={{ success: undefined }}
			onSubmit={handleSubmit}
			validationSchema={validationSchema()}
			validateOnChange={false}
			validateOnBlur={false}
		>
			{({
				setFieldValue,
				setFieldTouched,
				setFieldError,
				setStatus,
				isSubmitting,
				errors = {},
				status = {},
				values,
				submitCount,
			}) => {
				const handleFieldChange = (fieldName, value) => {
					setFieldValue(fieldName, value);
				};
				return (
					<Form
						className="newsletter-frequency__form"
						onChange={() => {
							setStatus({});
						}}
					>
						<FormErrorMessages
							errors={errors}
							isSubmitting={isSubmitting}
							submitCount={submitCount}
							fieldsFilter={["_error"]}
							disableErrorsDetails={true}
							message={<FormattedMessage id={errors?._error?.id} />}
						/>

						<div
							className="newsletter-frequency__frequency"
							data-testid="news-letter-frequency"
						>
							<Typography
								variant={TYPOGRAPHY_VARIANTS.LARGE}
								isBold
								className="newsletter-frequency__title"
							>
								<FormattedMessage id="subscriptions.intro" />
							</Typography>

							<FormErrorMessages
								errors={errors}
								isSubmitting={isSubmitting}
								submitCount={submitCount}
								alertType={ALERT_TYPE.ERROR_ICON}
								disableErrorsDetails={true}
								fieldsFilter={["frequency"]}
								message={<FormattedMessage id={errors?.frequency?.id} />}
							/>

							<div className="newsletter-frequency__frequency-list">
								{Object.keys(NEWSLETTER_FREQUENCIES).map(key => {
									const frequency = NEWSLETTER_FREQUENCIES[key];
									return (
										<RadioFormik
											key={key}
											showIcon={true}
											id={`frequency-${frequency}`}
											name="frequency"
											value={frequency}
											toggle={() => handleFieldChange("frequency", frequency)}
											className={classNames({
												"newsletter-frequency__frequency-input": true,
												"newsletter-frequency__frequency-input--error":
													errors?.frequency,
											})}
											data-testid={`newsletter-frequency-${frequency}`}
										>
											<FormattedMessage
												id={`newsletter.${frequency}.label`}
											/>
										</RadioFormik>
									);
								})}
							</div>
						</div>

						<div className="newsletter-frequency__remarketing">
							<Typography
								variant={TYPOGRAPHY_VARIANTS.LARGE}
								isBold
								className="newsletter-frequency__title"
							>
								<FormattedMessage id="subscriptions.remarketing.intro" />
							</Typography>

							<FormErrorMessages
								errors={errors}
								isSubmitting={isSubmitting}
								submitCount={submitCount}
								alertType={ALERT_TYPE.ERROR_ICON}
								disableErrorsDetails={true}
								fieldsFilter={["remarketing"]}
								message={<FormattedMessage id={errors?.remarketing?.id} />}
							/>

							<div className="newsletter-frequency__remarketing-list">
								{Object.keys(NEWSLETTER_REMARKETING).map(key => {
									const value = NEWSLETTER_REMARKETING[key];
									return (
										<RadioFormik
											key={key}
											showIcon={true}
											id={`remarketing-${value}`}
											name="remarketing"
											value={value}
											toggle={() => handleFieldChange("remarketing", value)}
											className={classNames({
												"newsletter-frequency__remarketing-input": true,
												"newsletter-frequency__remarketing-input--error":
													errors?.remarketing,
											})}
											data-testid={`remarketing-${value}`}
										>
											<FormattedMessage
												id={`newsletter.remarketing.${value}.label`}
											/>
										</RadioFormik>
									);
								})}
							</div>

							{displaySmsInput && (
								<>
									<Typography
										variant={TYPOGRAPHY_VARIANTS.LARGE}
										isBold
										className="newsletter-frequency__title"
									>
										<FormattedMessage id="subscriptions.remarketing.sms.intro" />
									</Typography>

									<FormErrorMessages
										errors={errors}
										isSubmitting={isSubmitting}
										submitCount={submitCount}
										alertType={ALERT_TYPE.ERROR_ICON}
										disableErrorsDetails={true}
										fieldsFilter={["sms"]}
										message={<FormattedMessage id={errors?.sms?.id} />}
									/>

									<div className="newsletter-frequency__sms-list">
										{Object.keys(NEWSLETTER_SMS).map(key => {
											const value = NEWSLETTER_SMS[key];
											return (
												<RadioFormik
													key={key}
													showIcon={true}
													id={`sms-${value}`}
													name="sms"
													value={value}
													toggle={() => handleFieldChange("sms", value)}
													className={classNames({
														"newsletter-frequency__sms-input": true,
														"newsletter-frequency__sms-input--error":
															errors?.remarketing,
													})}
													data-testid={`sms-${value}`}
												>
													<FormattedMessage
														id={`newsletter.remarketing.${value}.label`}
													/>
												</RadioFormik>
											);
										})}
									</div>
									{(values.sms === NEWSLETTER_SMS.ACCEPT ||
										values.sms === true) && (
										<div className="newsletter-frequency__phone">
											<FormErrorMessages
												errors={errors}
												isSubmitting={isSubmitting}
												submitCount={submitCount}
												alertType={ALERT_TYPE.ERROR_ICON}
												disableErrorsDetails={true}
												fieldsFilter={["phone"]}
												message={
													<FormattedMessage id={errors?.phone?.id} />
												}
											/>

											<div className="profile__phones__item">
												<PhoneIntl
													id="phone"
													name="phone"
													shop={shop}
													setFieldValue={setFieldValue}
													setFieldTouched={setFieldTouched}
													setFieldError={setFieldError}
													label={<FormattedMessage id="phone.label" />}
													data-testid="phone-input"
												/>
											</div>
										</div>
									)}
								</>
							)}
						</div>

						<div className="newsletter-frequency__action">
							{errors._error && (
								<AlertMessage
									alertType={ALERT_TYPE.ERROR}
									message={<FormattedMessage id={errors._error.id} />}
								/>
							)}

							<Button
								loading={isSubmitting}
								submit
								variant={BUTTON_VARIANTS.FILLED}
								size={BUTTON_SIZES.LARGE}
								label={<FormattedMessage id="subscriptions.button.label" />}
								data-testid="update-frenquency-button"
							/>

							{status.success && (
								<AlertMessage
									message={<FormattedMessage id="general.success.button.label" />}
									alertType={ALERT_TYPE.SUCCESS}
								/>
							)}
						</div>
					</Form>
				);
			}}
		</Formik>
	) : (
		false
	);
};

NewsLetterFrequency.propTypes = {
	handleSubmit: PropTypes.func,
	initialValues: PropTypes.object,
	shop: PropTypes.string,
	displaySmsInput: PropTypes.bool,
	loading: PropTypes.string,
};

export default NewsLetterFrequency;
